import { useEffect, useState } from "react";
import { Trophy, Medal, Flame, Target, Swords, Award } from "lucide-react";
import { api } from "@/lib/api";
import { EmptyState, Spinner } from "@/components/Bits";
import ClosingCard from "@/pages/league/ClosingCard";

function Badges({ leagueId }) {
  const [d, setD] = useState(null);
  useEffect(() => { api.get(`/leagues/${leagueId}/badges`).then(({ data }) => setD(data)); }, [leagueId]);
  if (!d) return null;
  const entries = Object.values(d.badges).flat();
  return (
    <section data-testid="league-badges">
      <h2 className="mb-3 flex items-center gap-2 text-sm font-bold"><Award size={15} className="text-[#B7F52A]" /> Badges</h2>
      {entries.length ? <div className="flex flex-wrap gap-2">{Object.entries(d.badges).map(([uid, list]) => list.map((b) => (
        <span key={uid + b.key} title={b.description} className="rounded-full border border-[#B7F52A]/30 bg-[#B7F52A]/[0.06] px-3 py-1 text-xs text-[#B7F52A]">{b.label}{b.count > 1 ? ` ×${b.count}` : ""} · <span className="text-slate-300">{b.name || ""}</span></span>)))}</div>
        : <p className="text-sm text-slate-400">Aucun badge pour l'instant.</p>}
      <details className="mt-2 text-xs text-slate-400"><summary className="cursor-pointer">Critères d'attribution</summary>{d.criteria.map((c) => <p key={c.key}><b className="text-slate-200">{c.label}</b> : {c.description}</p>)}</details>
    </section>
  );
}

export default function PalmaresTab({ league }) {
  const [data, setData] = useState(null);
  const [card, setCard] = useState(null);
  useEffect(() => { api.get(`/leagues/${league.id}/palmares`).then(({ data }) => setData(data)); }, [league.id]);
  if (!data) return <Spinner />;
  const periods = {};
  data.trophies.filter((t) => t.kind !== "podium").forEach((t) => { periods[t.period_key] = { ...(periods[t.period_key] || { label: t.period_label }), [t.kind]: t }; });
  const list = Object.entries(periods);
  return (
    <div className="space-y-6" data-testid="palmares">
      {card && <ClosingCard leagueId={league.id} periodKey={card} onClose={() => setCard(null)} />}
      <Badges leagueId={league.id} />
      {data.records?.best_streak && (
        <section className="grid gap-3 sm:grid-cols-2">
          <div className="mpl-card p-4"><p className="mpl-eyebrow flex items-center gap-1.5"><Flame size={12} /> Meilleure série</p><p className="mt-1 font-bold">{data.records.best_streak.name}</p><p className="font-mono-num text-[#B7F52A]">{data.records.best_streak.value} de suite</p></div>
          {data.records.best_accuracy && <div className="mpl-card p-4"><p className="mpl-eyebrow flex items-center gap-1.5"><Target size={12} /> Meilleur taux (10 matchs min.)</p><p className="mt-1 font-bold">{data.records.best_accuracy.name}</p><p className="font-mono-num text-[#B7F52A]">{data.records.best_accuracy.value} %</p></div>}
        </section>
      )}
      <section>
        <h2 className="mb-3 text-sm font-bold">Champions et lanternes rouges</h2>
        {list.length ? (
          <div className="grid gap-2">
            {list.map(([k, p]) => (
              <div key={k} className="mpl-card flex flex-wrap items-center justify-between gap-3 p-4" data-testid={`trophy-${k}`}>
                <p className="font-display text-lg font-extrabold uppercase">{p.label}</p>
                <div className="flex flex-wrap gap-3 text-sm">
                  {p.champion && <span className="flex items-center gap-1.5 text-[#B7F52A]"><Trophy size={15} /> {p.champion.name} ({p.champion.points})</span>}
                  {p.last && <span className="flex items-center gap-1.5 text-red-300"><Medal size={15} /> {p.last.name} ({p.last.points})</span>}
                  {p.champion?.start && <button onClick={() => setCard(k)} className="text-xs font-bold text-[#B7F52A]" data-testid={`closing-open-${k}`}>Carte de clôture</button>}
                </div>
              </div>
            ))}
          </div>
        ) : <EmptyState icon={Trophy} title="Aucune période archivée" text="À la fin de chaque période principale, le champion et le dernier sont inscrits ici. Les archives sont conservées d'une saison à l'autre." testId="palmares-empty" />}
      </section>
      <section>
        <h2 className="mb-3 text-sm font-bold">Défis terminés</h2>
        {data.challenges.length ? data.challenges.map((c) => (
          <div key={c.id} className="mpl-card mb-2 p-4" data-testid={`palmares-challenge-${c.id}`}>
            <div className="flex items-center justify-between gap-2"><p className="font-bold">{c.title}</p><span className={`text-[11px] font-bold uppercase ${c.status === "done" ? "text-[#B7F52A]" : "text-slate-400"}`}>{c.status === "done" ? "Réalisé" : "Terminé"}</span></div>
            <p className="mt-1 text-xs text-slate-400">{c.period_label} · {c.consequence}</p>
            {(c.losers.length > 0 || c.winners.length > 0) && <p className="mt-1 text-xs text-slate-200">{c.losers.length ? `Perdant : ${c.losers.join(", ")}` : `Gagnant : ${c.winners.join(", ")}`}</p>}
          </div>
        )) : <p className="flex items-center gap-2 text-sm text-slate-400"><Swords size={14} /> Aucun défi terminé pour l'instant.</p>}
      </section>
    </div>
  );
}
